/**
 * @fileoverview Update Notification Banner Component for Clockwise CPA
 *
 * This module provides a notification banner that alerts users when a new version
 * of the application has been deployed. It integrates with the version checking
 * system to display a prominent, dismissible notification that allows users to
 * refresh their browser to get the latest version.
 *
 * Key Features:
 * - Prominent fixed-position banner at top of viewport
 * - User-friendly messaging about available updates
 * - One-click refresh to get new version
 * - Dismissible with persistent state (localStorage)
 * - Automatic re-display when new version is deployed
 * - Accessibility compliant with ARIA attributes
 * - Bootstrap-based styling matching Clockwise design system
 *
 * User Interaction:
 * - Click banner to refresh and get new version
 * - Click dismiss (X) button to hide banner temporarily
 * - Dismissal is remembered for current version only
 * - Banner reappears when different version is deployed
 *
 * State Persistence:
 * - Uses localStorage to remember dismissals
 * - Key format: 'updateDismissed_${version}'
 * - Automatically clears when new version is detected
 * - Persists across browser sessions
 *
 * Accessibility Features:
 * - Proper ARIA role and labels for screen readers
 * - Keyboard navigation support
 * - Sufficient color contrast for visibility
 * - Focus management for interactive elements
 * - Semantic HTML structure
 *
 * Design Integration:
 * - Matches Clockwise CPA color scheme
 * - Uses Bootstrap utilities for consistency
 * - Responsive design for all screen sizes
 * - Professional appearance aligned with brand
 * - Smooth transitions and animations
 *
 * Security Considerations:
 * - No sensitive data displayed or stored
 * - localStorage usage is safe for dismissal state
 * - Version information is public and non-sensitive
 * - No XSS vulnerabilities (no dynamic HTML injection)
 *
 * Performance Considerations:
 * - Minimal DOM footprint when hidden
 * - Efficient localStorage operations
 * - No unnecessary re-renders
 * - Lightweight CSS animations
 *
 * @author Clockwise CPA Development Team
 * @version 1.0.0
 * @since 2024-01-01
 *
 * @requires react - For component functionality and hooks
 * @requires useVersionCheck - Custom hook for version monitoring
 *
 * @example
 * // Basic usage in App component
 * import UpdateNotificationBanner from './components/UpdateNotificationBanner';
 *
 * function App() {
 *   return (
 *     <div>
 *       <UpdateNotificationBanner />
 *       <AppContent />
 *     </div>
 *   );
 * }
 *
 * @example
 * // With custom check interval
 * <UpdateNotificationBanner checkInterval={120000} />
 */

import React, { useState, useEffect, useRef } from 'react';
import { useVersionCheck } from '../hooks/useVersionCheck';
import { useUserActivity } from '../hooks/useUserActivity';
import { FiRefreshCw, FiX } from 'react-icons/fi';
import { useLocation } from 'react-router-dom';
import '../styles/UpdateNotificationBanner.css';

/**
 * Inactivity timeout in milliseconds (10 minutes)
 * When user is inactive for this long, version checks are paused
 * @constant {number}
 */
const INACTIVITY_TIMEOUT = 10 * 60 * 1000;

/**
 * Update Notification Banner Component
 *
 * Displays a fixed-position banner at the top of the viewport when a new
 * application version is detected. Provides user controls to refresh the
 * page and get the new version, or dismiss the notification temporarily.
 *
 * Component Behavior:
 * - Monitors application version using useVersionCheck hook
 * - Shows banner when update is available and not dismissed
 * - Hides banner when user dismisses notification
 * - Reappears when new version is detected (different from dismissed version)
 * - Refreshes page when banner is clicked
 *
 * State Management:
 * - isDismissed: Local state controlling banner visibility
 * - updateAvailable: From useVersionCheck hook
 * - newVersion: From useVersionCheck hook
 * - Dismissal state persisted in localStorage
 *
 * LocalStorage Schema:
 * - Key: `updateDismissed_${version}`
 * - Value: 'true' (string)
 * - Lifetime: Until different version is detected
 *
 * Event Handlers:
 * - handleRefresh: Reloads page to get new version
 * - handleDismiss: Hides banner and stores dismissal
 *
 * @component
 * @param {Object} props - Component props
 * @param {number} [props.checkInterval] - Optional interval for version checks
 *   in milliseconds. Defaults to 5 minutes (300000ms).
 *
 * @returns {JSX.Element|null} The update notification banner or null if hidden
 *
 * @example
 * // Default usage
 * <UpdateNotificationBanner />
 *
 * @example
 * // With custom check interval (2 minutes)
 * <UpdateNotificationBanner checkInterval={120000} />
 *
 * @example
 * // Development mode with frequent checks
 * <UpdateNotificationBanner
 *   checkInterval={process.env.NODE_ENV === 'development' ? 30000 : 300000}
 * />
 */
const UpdateNotificationBanner = ({ checkInterval }) => {
  // Track user activity - pause version checks when user is inactive for 10 minutes
  const { isActive } = useUserActivity(INACTIVITY_TIMEOUT);

  // Use version check hook to monitor for updates (pauses when user is inactive)
  const { updateAvailable, newVersion } = useVersionCheck(checkInterval, isActive);

  // Get current location to track navigation
  const location = useLocation();

  // Ref to measure banner height
  const bannerRef = useRef(null);

  // Local state to track if user has dismissed the banner
  const [isDismissed, setIsDismissed] = useState(false);

  /**
   * Effect to check dismissal state from localStorage
   */
  useEffect(() => {
    if (newVersion) {
      // Check if user has dismissed this specific version
      const dismissedKey = `updateDismissed_${newVersion}`;
      const wasDismissed = localStorage.getItem(dismissedKey) === 'true';
      setIsDismissed(wasDismissed);
    }
  }, [newVersion]);

  /**
   * Effect to set CSS variable for banner height to push down topbar
   */
  useEffect(() => {
    if (updateAvailable && !isDismissed && bannerRef.current) {
      const height = bannerRef.current.offsetHeight;
      document.documentElement.style.setProperty('--update-banner-height', `${height}px`);
    } else {
      document.documentElement.style.setProperty('--update-banner-height', '0px');
    }

    // Cleanup on unmount
    return () => {
      document.documentElement.style.setProperty('--update-banner-height', '0px');
    };
  }, [updateAvailable, isDismissed]);

  /**
   * Effect to reload page on navigation if update is available
   * This prevents error boundaries from being triggered when navigating
   * while an update is pending
   */
  useEffect(() => {
    if (updateAvailable && !isDismissed) {
      // User navigated while update is available - reload to get new version
      // This prevents mismatched code chunks from causing errors
      window.location.reload(true);
    }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Handles the page refresh action
   *
   * Reloads the page to fetch and run the new version of the application.
   * Uses window.location.reload(true) to force a hard reload that bypasses
   * the browser cache and fetches all resources fresh from the server.
   *
   * Cache Bypass:
   * - true parameter forces hard reload
   * - Ensures new version is fetched completely
   * - Clears any cached application code
   *
   * @function
   * @returns {void}
   *
   * @example
   * // Called when user clicks banner
   * onClick={handleRefresh}
   */
  const handleRefresh = () => {
    // Force hard reload to bypass cache and get new version
    window.location.reload(true);
  };

  /**
   * Handles dismissing the update notification
   */
  const handleDismiss = (e) => {
    // Prevent click from bubbling to banner (which triggers refresh)
    e.stopPropagation();

    // Store dismissal in localStorage
    const dismissedKey = `updateDismissed_${newVersion}`;
    localStorage.setItem(dismissedKey, 'true');

    // Update local state to hide banner
    setIsDismissed(true);
  };

  // Don't render banner if no update available or user dismissed it
  if (!updateAvailable || isDismissed) {
    return null;
  }

  return (
    <div
      ref={bannerRef}
      className="update-notification-banner"
      role="alert"
      aria-live="polite"
      aria-atomic="true"
    >
      <div className="update-notification-content">
        <div className="update-notification-icon">
          <FiRefreshCw className="refresh-icon" />
        </div>
        <div className="update-notification-message">
          <button
            className="update-notification-action"
            onClick={handleRefresh}
            aria-label="Refresh page to get new version"
          >
            <span className="update-notification-text">
              A new version is available. Click here to update
            </span>
          </button>
        </div>
        <button
          className="update-notification-dismiss"
          onClick={handleDismiss}
          aria-label="Dismiss update notification"
          title="Dismiss this notification"
        >
          <FiX className="dismiss-icon" />
        </button>
      </div>
    </div>
  );
};

export default UpdateNotificationBanner;
